import React from 'react'
import { t } from 'i18next'
import { Close as ClearIcon, FileCopy as CopyIcon, PictureAsPdf as PdfIcon, Upload as UploadIcon } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useDispatch } from '../../store'
import { createQrRegistration } from '../../store/qrRegistration/actionCreator'
import { QrRegistrationResult } from '../../store/qrRegistration/actionType'
import { downloadQrRegistrationPdf } from './downloadQrRegistrationPdf'
import defaultBackgroundUrl from '../../asset/img/qr_code_background.jpg'

type Props = {
  onClose: () => void
  onCreated: () => void
}

// Bundled fallback poster background — used whenever the admin doesn't pick
// their own image, so a QR PDF never falls back to the plain gradient by
// default. CRA resolves the import above to a static asset URL; turning
// that into an actual File happens here (once per submit, not eagerly) so
// the network fetch only happens on the rare path where it's actually
// needed.
async function getDefaultBackgroundFile(): Promise<File> {
  const response = await fetch(defaultBackgroundUrl)
  const blob = await response.blob()
  return new File([blob], 'qr_code_background.jpg', { type: blob.type || 'image/jpeg' })
}

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png']
const MAX_IMAGE_SIZE = 1024 * 1024 * 4

// Mirrors PopupCreateEnterpriseUser.tsx's shape: a short form, then a
// "here's what was created" view — the join link only ever needs copying
// here, unlike an access key it isn't a secret, but showing it right after
// creation (plus an immediate PDF download) saves the admin a trip back
// into the table.
export function PopupCreateQrRegistration({ onClose, onCreated }: Props) {
  const dispatch = useDispatch()

  const uploadRef = React.useRef<HTMLInputElement>(null)

  const [label, setLabel] = React.useState('')
  // datetime-local input values ("2026-09-20T09:00") — both optional, a
  // blank side means "no boundary" on that side (see CreateQrRegistrationDto
  // on the backend).
  const [validFrom, setValidFrom] = React.useState('')
  const [validUntil, setValidUntil] = React.useState('')
  const [image, setImage] = React.useState<File>()
  const [imageError, setImageError] = React.useState('')
  const [creating, setCreating] = React.useState(false)
  const [result, setResult] = React.useState<QrRegistrationResult | null>(null)
  const [copied, setCopied] = React.useState(false)
  const [downloading, setDownloading] = React.useState(false)

  const handleUpload = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    setImageError('')

    const file = target.files?.[0]
    if (!file) return

    const extension = file.name.split('.').pop()?.toLowerCase() || ''
    if (IMAGE_EXTENSIONS.indexOf(extension) === -1) {
      setImageError(t('qrRegistration.create.backgroundImageTypeError'))
      return
    }
    if (file.size > MAX_IMAGE_SIZE) {
      setImageError(t('qrRegistration.create.backgroundImageSizeError'))
      return
    }

    setImage(file)
  }

  const handleClearImage = () => {
    if (uploadRef.current) uploadRef.current.value = ''
    setImage(undefined)
    setImageError('')
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setCreating(true)

    // No image picked -> use the bundled default (qr_code_background.jpg)
    // instead of leaving the poster on the plain gradient fallback.
    let file = image
    if (!file) {
      try {
        file = await getDefaultBackgroundFile()
      } catch {
        // Bundled asset failed to load (offline, blocked, etc.) — fall back
        // to no image rather than blocking QR-link creation over it; the
        // PDF still renders fine with the gradient background.
      }
    }

    dispatch(
      createQrRegistration(
        {
          label: label.trim() || undefined,
          validFrom: validFrom ? new Date(validFrom).toISOString() : undefined,
          validUntil: validUntil ? new Date(validUntil).toISOString() : undefined,
          file,
        },
        (data) => {
          setCreating(false)
          setResult(data)
        },
        () => setCreating(false),
      ),
    )
  }

  const handleCopy = () => {
    if (!result) return
    navigator.clipboard.writeText(result.joinUrl)
    setCopied(true)
  }

  const handleDownload = async () => {
    if (!result) return
    setDownloading(true)
    try {
      await downloadQrRegistrationPdf(result.id)
    } finally {
      setDownloading(false)
    }
  }

  const handleDone = () => {
    onCreated()
    onClose()
  }

  if (result) {
    return (
      <Dialog open={true} fullWidth onClose={handleDone}>
        <DialogTitle>{t('qrRegistration.create.title')}</DialogTitle>

        <Divider variant="popup" color="primary" />

        <Stack spacing={2} sx={{ p: 2 }}>
          <Typography variant="body2" color="textSecondary">
            {t('qrRegistration.hint')}
          </Typography>

          <FormControl fullWidth>
            <OutlinedInput
              value={result.joinUrl}
              readOnly
              sx={{ fontFamily: 'monospace' }}
              endAdornment={
                <InputAdornment position="end">
                  <Tooltip title={<span>{copied ? t('common.copied') : t('common.copy')}</span>}>
                    <IconButton onClick={handleCopy} size="small">
                      <CopyIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              }
            />
          </FormControl>

          <LoadingButton
            onClick={handleDownload}
            loading={downloading}
            variant="outlined"
            color="primary"
            startIcon={<PdfIcon />}
          >
            {t('qrRegistration.action.downloadPdf')}
          </LoadingButton>
        </Stack>

        <Divider variant="popup" color="primary" />

        <DialogActions>
          <Button onClick={handleDone} variant="contained" size="large" color="primary">
            {t('common.done')}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Dialog open={true} fullWidth onClose={creating ? undefined : onClose}>
      <DialogTitle>{t('qrRegistration.create.title')}</DialogTitle>

      <Divider variant="popup" color="primary" />

      <form onSubmit={handleSubmit} noValidate>
        <Stack spacing={2} sx={{ p: 2 }}>
          <FormControl fullWidth>
            <FormLabel>
              {t('qrRegistration.table.label')}{' '}
              <Typography component="span" variant="caption" color="textSecondary">
                ({t('common.optional')})
              </Typography>
            </FormLabel>
            <OutlinedInput
              value={label}
              placeholder="Lobby poster"
              autoFocus
              disabled={creating}
              inputProps={{ maxLength: 120 }}
              onChange={(e) => setLabel(e.target.value)}
            />
            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
              {t('qrRegistration.create.labelHint')}
            </Typography>
          </FormControl>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <FormLabel>
                  {t('qrRegistration.create.validFrom')}{' '}
                  <Typography component="span" variant="caption" color="textSecondary">
                    ({t('common.optional')})
                  </Typography>
                </FormLabel>
                <OutlinedInput
                  type="datetime-local"
                  value={validFrom}
                  disabled={creating}
                  onChange={(e) => setValidFrom(e.target.value)}
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <FormLabel>
                  {t('qrRegistration.create.validUntil')}{' '}
                  <Typography component="span" variant="caption" color="textSecondary">
                    ({t('common.optional')})
                  </Typography>
                </FormLabel>
                <OutlinedInput
                  type="datetime-local"
                  value={validUntil}
                  disabled={creating}
                  inputProps={{ min: validFrom || undefined }}
                  onChange={(e) => setValidUntil(e.target.value)}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Typography variant="caption" color="textSecondary" sx={{ mt: -1.5 }}>
            {t('qrRegistration.create.validityHint')}
          </Typography>

          <FormControl fullWidth>
            <FormLabel>
              {t('qrRegistration.create.backgroundImage')}{' '}
              <Typography component="span" variant="caption" color="textSecondary">
                ({t('common.optional')})
              </Typography>
            </FormLabel>
            <input
              ref={uploadRef}
              type="file"
              accept="image/png,image/jpeg"
              style={{ display: 'none' }}
              onChange={handleUpload}
            />
            {image ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                  {image.name}
                </Typography>
                <IconButton size="small" onClick={handleClearImage} disabled={creating}>
                  <ClearIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
            ) : (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<UploadIcon sx={{ fontSize: 18 }} />}
                disabled={creating}
                onClick={() => uploadRef.current?.click()}
                sx={{ alignSelf: 'flex-start' }}
              >
                {t('qrRegistration.create.backgroundImageUpload')}
              </Button>
            )}
            {imageError && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {imageError}
              </Typography>
            )}
            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
              {t('qrRegistration.create.backgroundImageHint')}
            </Typography>
          </FormControl>
        </Stack>

        <Divider variant="popup" color="primary" />

        <DialogActions>
          <Button onClick={onClose} color="primary" size="large" disabled={creating}>
            {t('common.close')}
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            size="large"
            color="primary"
            loading={creating}
          >
            {t('common.save')}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  )
}
